<template>
  <el-menu
    :default-active="activeMenuIndex"
    class="personal-home-menu"
  >
    <div v-for="(menu, key) in menuGroup" class="menu-list" :key="key">
      <template v-if="menu?.length > 0">
        <span class="group-label">
          {{ $t(menuLabelGroup[key]) }}
        </span>
        <el-menu-item
          v-for="(item, index) in menu"
          :key="index"
          :index="`${key}-${index}`"
          @click="handleClick(item)"
        >
          <template slot="title">
            <span class="menu-label">{{ getMenuLabel(item) }}</span>
            <el-badge v-if="item.meta?.showBadge" class="menu-badge" :value="getBadgeCount(item.meta.showBadge)"></el-badge>
          </template>
        </el-menu-item>
      </template>
    </div>
  </el-menu>
</template>

<script>
import _ from 'lodash'
import { personalCompleteApplicationRoute, personalIncompleteApplicationRoute } from '@/views/personalNew/home/applicationsRoute'
import homeRoute from '@/views/personalNew/home/homeRoute'
import { PersonalHomeConsentRoute } from '@/views/personalNew/home/consentRoute'
import profileRoute from '@/views/personalNew/home/profileRoute'
import reportRoute from '@/views/personalNew/home/reportRoute'
import referralsRoute, { rewardsRoute, leaderBoardRoute } from '@/views/personalNew/home/referralsRoute'
import referralProgramRoute from '@/views/personalNew/home/referralProgram'
import functionsRoute from '@/views/personalNew/home/functionsRoute'
import offerLetterRoute from '@/views/personalNew/home/offerLetterRoute'
import { mixedRouterPush } from '@/router/routes'
import { formatBigNumber } from '@/components/filters'
import { addQueryInPath } from '@/utils/common'
import mixin from '@/views/personalNew/home/mixin'

const BADGE_MAP = {
  incomplete: 'pending_application_count'
}

const CUSTOMIZED_MENU_ROOT_COMPANY_ID = [13509]

export default {
  props: {
    // homePageInfo: {
    //   type: Object,
    //   required: true
    // },
    account: {
      type: Object,
      required: true
    },
    personalInfo: {
      type: Object,
      required: true
    }
  },
  mixins: [mixin],
  data () {
    return {
      i18nPrefix: 'personal.menu',
      menuGroup: {},
      menuLabelGroup: {},
      activeMenuIndex: '-1'
    }
  },
  computed: {
    companyId () {
      return this.$route.query.company_id
    },
    menuMap () {
      const employee = this.personalInfo.employee
      let dashboardMenu = [
        homeRoute,
        PersonalHomeConsentRoute,
        {
          hidden: !this.personalInfo.preference_survey?.show_entrance,
          titleI18nKey: 'personal.menu.jobAlert',
          permission: employee ? ['COMPUTED_ACCOUNT_IS_EMPLOYEE'] : null,
          handleClick: () => {
            const companyId = employee ? employee.company.id : this.companyId
            this.$router.push(`/recruiting/company/${companyId}/survey`)
          }
        },
        reportRoute,
        profileRoute,
        personalCompleteApplicationRoute,
        personalIncompleteApplicationRoute,
        offerLetterRoute
      ]
      const hiddenRewards = this.personalInfo?.employee?.company?.root_company?.id === 12600
      const referralsChildrenRoute = _.filter([...referralsRoute.children, referralProgramRoute], item => !hiddenRewards || !['/recruiting/personal/referral/program', '/recruiting/personal/referral/rewards'].includes(item.path))
      dashboardMenu = _.filter(dashboardMenu, item => !item.hidden)
      const defaultChildRoute = [...referralsChildrenRoute, ...functionsRoute.children]
      const customRoute = {}

      const query = {}
      _.forEach(this.$route.query, (value, key) => {
        if (value !== null && value !== undefined) {
          query[key] = value
        }
      })
      const queryParams = new URLSearchParams(query).toString()
      const isEmployee = this.personalInfo.employee
      if (this.pcCustomMenuList) {
        _.forEach(this.pcCustomMenuList, (item, index) => {
          if (!item.isActive || (item.onlyEmployee && !isEmployee) || item.children.length === 0) {
            return
          }
          const childrenMenu = []
          const activeMenu = _.filter(item.children, child => child.isActive && (!child.onlyEmployee || isEmployee))
          _.forEach(activeMenu, (child) => {
            if (child.isDefault) {
              const route = _.find(defaultChildRoute, { path: child.link })
              if (!route) {
                return
              }
              childrenMenu.push(route)
            } else {
              let path = child.link
              if (queryParams) {
                path = `${path}?${queryParams}`
              }
              childrenMenu.push({
                title: child.label,
                handleClick: () => {
                  window.location.href = path
                }
              })
            }
          })
          const routeKey = item.key || `customMenu-${index}`
          customRoute[routeKey] = { children: childrenMenu, title: item.name }
          if (routeKey === 'referrals') {
            customRoute[routeKey].menuI18nKey = referralsRoute.meta.menuI18nKey
          } else if (routeKey === 'functions') {
            customRoute[routeKey].menuI18nKey = functionsRoute.meta.menuI18nKey
          }
        })
      } else {
        customRoute.referrals = { route: { ...referralsRoute, children: referralsChildrenRoute } }
        customRoute.functions = { route: functionsRoute }
      }
      return {
        dashboard: { children: dashboardMenu, menuI18nKey: 'personal.menu.group.dashboard' },
        ...customRoute
      }
    }
  },
  created () {
    this.loadMenu()
    this.loadActiveMenu()
  },
  methods: {
    getMenuLabel (menu) {
      if (menu.meta) {
        const meta = menu.meta
        if ((meta.checkHasMobile && !this.personalInfo.mobile) || (meta.checkHasEmail && !this.personalInfo.email_address)) {
          return this.$t(meta.checkFailI18nKey)
        }
        return meta.title || this.$t(meta.titleI18nKey)
      } else {
        return menu.title || this.$t(menu.titleI18nKey)
      }
    },
    loadMenu () {
      const menuGroup = {}
      const menuLabelGroup = {}
      _.forEach(this.menuMap, (menu, key) => {
        if (menu.children) {
          menuGroup[key] = _.filter(menu.children, (item) => {
            let permission
            let employeePermission
            if (item.meta) {
              permission = item.meta.permission
              employeePermission = item.meta.employeePermission
            } else {
              permission = item.permission
              employeePermission = item.employeePermission
            }
            permission = this.hasAnyPermission(permission)
            if (this.personalInfo.employee) {
              employeePermission = this.hasAnyPermission(employeePermission)
            } else {
              employeePermission = true
            }
            return permission && employeePermission
          })
        } else {
          menuGroup[key] = _.filter(menu.route.children, (route) => {
            const permission = this.hasAnyPermission(route.meta.permission)
            let employeePermission = true
            let isDisplayMenu = true
            if (this.personalInfo.employee) {
              employeePermission = this.hasAnyPermission(route.meta.employeePermission)
              if ([rewardsRoute, leaderBoardRoute].includes(route)) {
                const rootCompanyId = this.personalInfo.employee.company?.root_company?.id || this.personalInfo.employee.company?.id
                isDisplayMenu = !CUSTOMIZED_MENU_ROOT_COMPANY_ID.includes(rootCompanyId)
              }
            }
            return permission && employeePermission && isDisplayMenu
          })
        }
        menuLabelGroup[key] = menu.menuI18nKey || menu.title || menu.route.meta.menuI18nKey
      })
      this.menuGroup = menuGroup
      this.menuLabelGroup = menuLabelGroup
    },
    handleClick (route) {
      const routePath = route.path
      const queryCompanyId = this.$route.query?.company_id
      const path = addQueryInPath(routePath, queryCompanyId ? { company_id: queryCompanyId } : null, true)
      if (this.$route.path === routePath) {
        this.reloadPage()
      } else {
        if (route.handleClick) {
          return route.handleClick()
        }
        if (!route.meta) {
          return mixedRouterPush(path)
        }
        let meta = route.meta
        let isAllInclude = false
        while (meta) {
          if (meta.isAllInclude) {
            isAllInclude = true
            break
          }
          meta = meta._parent?.meta
        }
        if (isAllInclude) {
          this.$router.push(path)
        } else if (route.meta.isInclude) {
          mixedRouterPush(path)
        } else {
          location.href = path
        }
      }
    },
    getBadgeCount (badge) {
      const count = this.personalInfo?.[BADGE_MAP[badge]]
      if (count && count > 0) {
        return formatBigNumber(count)
      }
      return null
    },
    loadActiveMenu () {
      const route = this.$route
      const routePathList = [route.path]
      let routePath = routePathList[0]
      let metaInfo = this.$route.meta
      while ((routePath = metaInfo._parent?.path)) {
        metaInfo = metaInfo._parent?.meta
        routePathList.push(routePath)
      }
      _.forEach(this.menuGroup, (menu, key) => {
        _.forEach(menu, (item, index) => {
          if (routePathList.includes(item.path)) {
            this.activeMenuIndex = `${key}-${index}`
          }
        })
      })
    }
  },
  watch: {
    $route: {
      handler () {
        this.loadActiveMenu()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.personal-home-menu {
  overflow: auto;

  .menu-list {
    padding: 24px 0 14px 0;

    .group-label {
      display: inline-block;
      color: #303133;
      line-height: 22px;
      font-size: 16px;
      margin: 0 0 12px 14px;
    }

    .menu-label {
      margin-left: 6px;
      font-size: 14px;
      color: #606266;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .menu-badge {
      ::v-deep .el-badge__content {
        background-color: #0096cc;
        height: initial;
        vertical-align: top;
      }
    }

    ::v-deep {
      .el-menu-item {
        font-size: 14px;
        color: #606266;
        height: 42px;
        line-height: 42px;
      }

      .is-active {
        background-color: #f2fcff;
        .menu-label {
          color: #0096cc;
        }
      }
    }
  }
}

</style>
